import { DashboardTabKey, USER_ROLES } from "@nlc/shared-types";

import { Skeleton } from "@/components/skeleton";
import { useUser } from "@/hooks/useUser";

import { DashboardCard } from "./components/DashboardCard";
import { DASHBOARD_TAB_CONFIG } from "./config";
import { useDashboardSummary } from "./hooks/useDashboardSummary";

function getTabConfig(key: DashboardTabKey, role?: null | string) {
  const config = DASHBOARD_TAB_CONFIG[key];

  if (
    role === USER_ROLES.DESIGNER &&
    (key === DashboardTabKey.DUE_TODAY || key === DashboardTabKey.REVISING)
  ) {
    return { ...config, search: { tab: "assigned-to-me" } };
  }

  if (
    role === USER_ROLES.TEAM_LEADER &&
    (key === DashboardTabKey.DUE_TODAY ||
      key === DashboardTabKey.PROCESSING ||
      key === DashboardTabKey.OVERDUE)
  ) {
    return { ...config, search: { tab: "all-requests" } };
  }

  return config;
}

export function DashboardPage() {
  const { data, isError, isLoading, refetch } = useDashboardSummary();
  const { data: userData } = useUser();
  const userRole = userData?.user?.roleName;

  if (isLoading) {
    return (
      <div className="flex flex-col sm:flex-row gap-3 p-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton className="flex-1 h-48 rounded-3xl" key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4">
        <p className="text-sm text-text-secondary">
          Failed to load dashboard summary.
        </p>
        <button
          className="mt-2 text-sm text-button-primary-bg hover:underline"
          onClick={() => refetch()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  const tabs = (data?.tabs ?? [])
    .filter((tab) => DASHBOARD_TAB_CONFIG[tab.key] !== undefined)
    .sort(
      (a, b) =>
        DASHBOARD_TAB_CONFIG[a.key].displayOrder -
        DASHBOARD_TAB_CONFIG[b.key].displayOrder,
    );

  if (tabs.length === 0) return null;

  const featuredTab = tabs.find((t) => DASHBOARD_TAB_CONFIG[t.key].featured);
  const otherTabs = tabs.filter((t) => !DASHBOARD_TAB_CONFIG[t.key].featured);

  // Team Leader: jumbo featured card left + 2x2 grid right
  if (featuredTab && otherTabs.length === 4) {
    const [tl, tr, bl, br] = otherTabs as [
      (typeof otherTabs)[0],
      (typeof otherTabs)[0],
      (typeof otherTabs)[0],
      (typeof otherTabs)[0],
    ];

    return (
      <>
        <div className="flex flex-col lg:flex-row gap-3 p-4">
          <DashboardCard
            config={getTabConfig(featuredTab.key, userRole)}
            count={featuredTab.count}
            jumbo
          />
          <div className="grid grid-cols-2 gap-3 flex-1">
            <DashboardCard
              config={getTabConfig(tl.key, userRole)}
              count={tl.count}
            />
            <DashboardCard
              config={getTabConfig(tr.key, userRole)}
              count={tr.count}
            />
            <DashboardCard
              config={getTabConfig(bl.key, userRole)}
              count={bl.count}
            />
            <DashboardCard
              config={getTabConfig(br.key, userRole)}
              count={br.count}
            />
          </div>
        </div>
        <div className="flex justify-center text-utility-gray-400 mt-4">
          This is just the beginning. More enhancements are coming soon.
        </div>
      </>
    );
  }

  // Default: equal-width row of cards (Super Admin, Designer, Customer Success)
  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 p-4">
        {tabs.map((tab) => (
          <DashboardCard
            config={getTabConfig(tab.key, userRole)}
            count={tab.count}
            key={tab.key}
          />
        ))}
      </div>
      <div className="flex justify-center text-utility-gray-400 mt-4">
        This is just the beginning. More enhancements are coming soon.
      </div>
    </>
  );
}
